import React from 'react';
import cx from 'classnames';
import { localPoint } from '@visx/event';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { Icon } from 'js/components';
import { faSitemap } from '@fortawesome/pro-solid-svg-icons';
import CapabilityOverviewBarSvg from './CapabilityOverviewBarSvg';
import CapabilityOverviewBarTooltip from './CapabilityOverviewBarTooltip';
import styles from './CapabilityOverviewCharts.module.scss';

export default function CapabilityOverviewBar({
  capability,
  element,
  metrics,
  capabilitySetName,
  threshold,
  width,
  height,
  itemIndex = 0,
}) {
  const depth = capability?.depth ?? 0;
  const tierIndex = depth + 1;
  const fullPath = capability?.path?.length
    ? capability.path
    : [capability?.name].filter(Boolean);
  const ancestorPath = fullPath.slice(0, -1);
  const capabilityName = fullPath[fullPath.length - 1] || capability?.name;
  const paletteIndex = element?.paletteIndex ?? 2;

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const handleMouseMove = (event) => {
    const coords = localPoint(event.currentTarget, event);
    if (!coords) {
      return;
    }
    showTooltip({
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
      tooltipData: {
        ancestorPath,
        capabilityName,
        capabilitySetName,
        metrics,
        elementName: element?.name || element?.key,
      },
    });
  };

  return (
    <div
      ref={containerRef}
      className={styles['overview-bar']}
      onMouseMove={handleMouseMove}
      onMouseLeave={hideTooltip}
    >
      <CapabilityOverviewBarSvg
        element={element}
        metrics={metrics}
        threshold={threshold}
        width={width}
        height={height}
        itemIndex={itemIndex}
      >
        <div
          className={cx(
            styles['capability-tier'],
            `palette-tiers-${tierIndex}`,
            'text-only'
          )}
        >
          <Icon icon={faSitemap} className={styles['capability-tier-icon']} />
          {tierIndex}
        </div>
      </CapabilityOverviewBarSvg>
      <div className={styles['capability-label']}>
        <div
          className={cx(styles['capability-name'], 'line-clamp-var')}
          style={{ '--line-clamp': 4 }}
        >
          {capability?.name}
        </div>
      </div>
      <CapabilityOverviewBarTooltip
        TooltipInPortal={TooltipInPortal}
        tooltipOpen={tooltipOpen}
        tooltipData={tooltipData}
        tooltipLeft={tooltipLeft}
        tooltipTop={tooltipTop}
        paletteIndex={paletteIndex}
      />
    </div>
  );
}
